import { Icon } from '../common/Icon';

export interface PillTab<T extends string> {
  value: T;
  label: string;
  icon?: string;
}

export interface PillTabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: PillTab<T>[];
  /** Visual variant. `flat` (default) is a row of pills sitting on
   * the page background — what you want inside `<PageHeader>`. `inset`
   * wraps them in a soft gray track, the WeChat segmented-control look. */
  variant?: 'flat' | 'inset';
}

/**
 * Horizontal pill-style tab control. Used as page-level mode toggles
 * (Messages | Agent Social, 推荐 | 关注 | 社区, etc.) and for sort
 * dimensions on leaderboards. Always render inside a `<PageHeader>`'s
 * `children` slot — never as a freestanding row.
 *
 * @example
 * <PillTabs
 *   value={mode}
 *   onChange={setMode}
 *   options={[
 *     { value: 'human-bot', label: 'Messages',     icon: 'chat' },
 *     { value: 'bot-bot',   label: 'Agent Social', icon: 'forum' },
 *   ]}
 * />
 */
export function PillTabs<T extends string>({
  value,
  onChange,
  options,
  variant = 'flat',
}: PillTabsProps<T>) {
  const containerClass =
    variant === 'inset'
      ? 'inline-flex items-center gap-1 bg-gray-100 dark:bg-white/[0.06] rounded-full p-0.5'
      : 'flex items-center gap-1.5 overflow-x-auto hide-scrollbar';

  return (
    <div className={containerClass}>
      {options.map((opt) => {
        const active = value === opt.value;
        const baseClass = variant === 'inset'
          ? `flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-colors ${
              active
                ? 'bg-white dark:bg-white/[0.12] text-gray-900 dark:text-gray-100 font-medium shadow-sm'
                : 'text-gray-500 dark:text-gray-400'
            }`
          : `flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              active
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06]'
            }`;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={baseClass}
          >
            {opt.icon && <Icon name={opt.icon} size={13} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
